
import React, { useEffect, useState, useRef } from 'react';
import { Card, Button, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { Save, RefreshCw, AlertTriangle, CheckCircle, Search, X } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { PageHero } from '../components/PageHero';

interface GridEntry {
    user_id: string;
    team_id: string | null;
    is_reserve: boolean;
    username: string;
    team_name: string;
    team_color: string;
    team_logo?: string;
    car_image_url?: string;
    avatarUrl?: string; 
    attendance?: 'ATTENDING' | 'ABSENT' | 'NO_RESPONSE';
    ea_id?: string;
}

interface TeamBucket {
    team_id: string;
    team_name: string;
    team_color: string;
    team_logo?: string;
    car_image_url?: string;
    driver1: GridEntry | null;
    driver2: GridEntry | null;
}

const DriverAutocomplete: React.FC<{
    drivers: GridEntry[];
    onSelect: (userId: string) => void;
    placeholder?: string;
}> = ({ drivers, onSelect, placeholder }) => {
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filtered = drivers.filter(d => 
        d.username.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className={`relative w-full flex-1 ${isOpen ? 'z-[100]' : 'z-auto'}`} ref={wrapperRef}>
            <div className="relative">
                <input 
                    className="w-full bg-gray-900/50 border border-dashed border-gray-600 rounded p-1.5 pl-7 text-xs text-white focus:border-f1-red focus:bg-gray-900 outline-none transition-colors placeholder-gray-500"
                    placeholder={placeholder || "Fahrer hinzufügen …"}
                    value={search}
                    onChange={e => { setSearch(e.target.value); setIsOpen(true); }}
                    onFocus={() => setIsOpen(true)}
                />
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500 pointer-events-none" />
                {search && (
                    <button onClick={() => { setSearch(''); setIsOpen(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"><X size={12}/></button>
                )}
            </div>
            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-500 rounded-md shadow-2xl overflow-y-auto max-h-60 w-full min-w-[200px]">
                    {filtered.length === 0 ? <div className="p-2 text-[10px] text-gray-500 italic text-center">No match</div> : 
                        filtered.map(d => (
                            <div key={d.user_id} onClick={() => { onSelect(d.user_id); setSearch(''); setIsOpen(false); }} className="px-2 py-2 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-0 flex justify-between items-center group">
                                <span className="text-xs font-bold text-gray-200 group-hover:text-white">{d.username}</span>
                                {d.attendance !== 'ATTENDING' && <span className="text-[10px] text-gray-500">Not Reg.</span>}
                            </div>
                        ))
                    }
                </div>
            )}
        </div>
    );
};

export const StartingGrid: React.FC = () => {
    const [events, setEvents] = useState<any[]>([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [buckets, setBuckets] = useState<TeamBucket[]>([]);
    const [reservePool, setReservePool] = useState<GridEntry[]>([]);
    const [absentPool, setAbsentPool] = useState<GridEntry[]>([]); 
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({ attending: 0, total: 0 });

    useEffect(() => {
        apiRequest<any[]>('events.php').then(data => {
            const active = data.filter((e: any) => e.status !== 'COMPLETED');
            setEvents(active);
            if(active.length > 0) loadGrid(active[0].id);
        });
    }, []);

    const loadGrid = async (eventId: string) => {
        setLoading(true);
        setSelectedEventId(eventId);
        try {
            const res = await apiRequest<any>(`event_entries.php?eventId=${eventId}`);
            const seasonDrivers: any[] = res.seasonDrivers || []; 
            const savedEntries: any[] = res.savedEntries || [];
            const seasonTeams: any[] = res.seasonTeams || [];
            const hasSaved = res.hasSavedGrid;

            let attendingCount = 0;
            seasonDrivers.forEach(d => { if (d.attendance_status === 'ATTENDING') attendingCount++; });
            setStats({ attending: attendingCount, total: seasonDrivers.length });

            const teamsMap = new Map<string, TeamBucket>();
            seasonTeams.forEach(t => {
                teamsMap.set(t.id, {
                    team_id: t.id,
                    team_name: t.name,
                    team_color: t.color,
                    team_logo: t.team_logo,
                    car_image_url: t.car_image_url,
                    driver1: null,
                    driver2: null
                });
            });

            const available: GridEntry[] = [];
            const unavailable: GridEntry[] = [];
            
            const processEntry = (sd: any) => ({
                user_id: sd.user_id, team_id: sd.team_id, is_reserve: (sd.is_reserve == 1 || sd.is_reserve === '1'), username: sd.username, ea_id: sd.ea_id,
                team_name: sd.team_name, team_color: sd.team_color, team_logo: sd.team_logo, car_image_url: sd.car_image_url,
                avatarUrl: sd.avatar_url, attendance: (sd.attendance_status || 'NO_RESPONSE') as any
            });

            if (hasSaved) {
                 savedEntries.forEach((entry: any) => {
                    const gridEntry: GridEntry = {
                        user_id: entry.user_id, team_id: entry.team_id, is_reserve: (entry.is_reserve == 1 || entry.is_reserve === '1'), username: entry.username, ea_id: entry.ea_id,
                        team_name: entry.team_name || 'Reserve', team_color: entry.team_color || '#666', team_logo: entry.team_logo, avatarUrl: entry.avatar_url,
                        attendance: (entry.attendance_status || 'NO_RESPONSE') as any
                    };
                    if (entry.team_id && teamsMap.has(entry.team_id)) {
                        const bucket = teamsMap.get(entry.team_id)!;
                        if (!bucket.driver1) bucket.driver1 = gridEntry;
                        else if (!bucket.driver2) bucket.driver2 = gridEntry;
                        else available.push(gridEntry); 
                    } else { available.push(gridEntry); }
                });
                seasonDrivers.forEach(sd => {
                    if(!savedEntries.find((se:any) => se.user_id === sd.user_id)) {
                        const entry = processEntry(sd);
                        if (entry.attendance === 'ATTENDING') available.push(entry); else unavailable.push(entry);
                    }
                });
            } else {
                seasonDrivers.forEach(sd => {
                    const entry = processEntry(sd);
                    if (entry.attendance === 'ATTENDING') {
                        // FIX: Use entry properties (sanitized) instead of sd (raw string values)
                        if (entry.team_id && !entry.is_reserve && teamsMap.has(entry.team_id)) {
                            const bucket = teamsMap.get(entry.team_id)!;
                            if (!bucket.driver1) bucket.driver1 = entry; else if (!bucket.driver2) bucket.driver2 = entry; else available.push(entry);
                        } else { available.push(entry); }
                    } else { unavailable.push(entry); }
                });
            }
            setBuckets(Array.from(teamsMap.values()));
            setReservePool(available.sort((a,b) => a.username.localeCompare(b.username)));
            setAbsentPool(unavailable.sort((a,b) => a.username.localeCompare(b.username)));
        } catch (e) { console.error(e); } finally { setLoading(false); }
    };

    const handleAssign = (userId: string, teamId: string, slot: 1 | 2) => {
        let driverIdx = reservePool.findIndex(d => d.user_id === userId);
        let sourcePool = 'reserve';
        if (driverIdx === -1) { driverIdx = absentPool.findIndex(d => d.user_id === userId); sourcePool = 'absent'; }
        if (driverIdx === -1) return;
        const driver = sourcePool === 'reserve' ? reservePool[driverIdx] : absentPool[driverIdx];
        const newBuckets = [...buckets]; const newReserve = [...reservePool]; const newAbsent = [...absentPool];
        const bucket = newBuckets.find(b => b.team_id === teamId)!;
        const currentOccupant = slot === 1 ? bucket.driver1 : bucket.driver2;
        if (currentOccupant) newReserve.push({...currentOccupant, team_id: null, is_reserve: true});
        const newEntry = {...driver, team_id: teamId, is_reserve: driver.is_reserve}; 
        if (slot === 1) bucket.driver1 = newEntry; else bucket.driver2 = newEntry;
        if (sourcePool === 'reserve') newReserve.splice(driverIdx, 1); else newAbsent.splice(driverIdx, 1);
        setBuckets(newBuckets); setReservePool(newReserve); setAbsentPool(newAbsent);
    };

    const handleUnassign = (teamId: string, slot: 1 | 2) => {
        const newBuckets = [...buckets]; const newPool = [...reservePool];
        const bucket = newBuckets.find(b => b.team_id === teamId)!;
        const occupant = slot === 1 ? bucket.driver1 : bucket.driver2;
        if (!occupant) return;
        newPool.push({...occupant, team_id: null}); 
        if (slot === 1) bucket.driver1 = null; else bucket.driver2 = null;
        setBuckets(newBuckets); setReservePool(newPool);
    };

    const saveGrid = async () => {
        const entries: any[] = [];
        buckets.forEach(b => {
            if(b.driver1) entries.push({ user_id: b.driver1.user_id, team_id: b.team_id, is_reserve: b.driver1.is_reserve });
            if(b.driver2) entries.push({ user_id: b.driver2.user_id, team_id: b.team_id, is_reserve: b.driver2.is_reserve });
        });
        try {
            await apiRequest('event_entries.php', 'POST', { eventId: selectedEventId, entries });
            alert("Startaufstellung gespeichert!");
        } catch (e: any) { alert(e.message); }
    };

    const selectedEvent = events.find(e => e.id === selectedEventId);
    const allAvailableDrivers = [...reservePool, ...absentPool];

    return (
        <div className="mx-auto max-w-7xl space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow={selectedEvent?.trackLocation || 'Race Entry Control'}
                title="Startaufstellung"
                description="Stamm- und Ersatzfahrer für das ausgewählte Rennen prüfen und die endgültigen Grid-Plätze festlegen."
                icon={CheckCircle}
                accent="#22c55e"
                stats={[{ label: 'Zugesagt', value: stats.attending }, { label: 'Fahrer', value: stats.total }, { label: 'Reservepool', value: reservePool.length }]}
                actions={(
                    <div className="flex gap-2">
                    <select className="bg-gray-900 border border-gray-700 rounded p-2 text-white font-bold" value={selectedEventId} onChange={e => loadGrid(e.target.value)}>
                        {events.map(e => <option key={e.id} value={e.id}>{e.trackLocation}</option>)}
                    </select>
                    <Button variant="secondary" icon={RefreshCw} onClick={() => loadGrid(selectedEventId)}>Neu laden</Button>
                    </div>
                )}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="space-y-6">
                    <Card title="Reservepool (zugesagt)" className="rounded-[1.5rem] border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017]">
                        <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2">
                            {reservePool.map(driver => (
                                <div key={driver.user_id} className="p-2 rounded border border-gray-700 bg-green-900/10 flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        <div className="w-1 h-8 rounded-l" style={{backgroundColor: driver.team_color}}></div>
                                        <div><div className="font-bold text-sm text-white">{driver.username}</div><div className="text-[10px] uppercase text-gray-500">{driver.team_name}</div></div>
                                    </div>
                                    <Badge color="green">IN</Badge>
                                </div>
                            ))}
                            {reservePool.length === 0 && <div className="text-gray-500 italic text-sm text-center">Pool ist leer</div>}
                        </div>
                    </Card>
                    <Card title="Nicht angemeldet / verspätet" className="rounded-[1.5rem] border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017]">
                         <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2">
                            {absentPool.map(driver => (
                                <div key={driver.user_id} className={`p-2 rounded border flex justify-between items-center ${driver.attendance === 'ABSENT' ? 'bg-red-900/10 border-red-900/30' : 'bg-gray-800 border-gray-700'}`}>
                                    <div><div className="font-bold text-sm text-gray-300">{driver.username}</div><div className="text-[10px] uppercase text-gray-500">{driver.attendance === 'NO_RESPONSE' ? 'No Response' : 'Absent'}</div></div>
                                    <div className="text-xs text-gray-600">Avail.</div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

                <Card className="lg:col-span-2" title="Starting Grid Slots">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {buckets.map(bucket => (
                            <div key={bucket.team_id} className="bg-gray-900 border border-gray-800 rounded relative">
                                <div className="h-1 w-full rounded-t" style={{backgroundColor: bucket.team_color}}></div>
                                <div className="p-2 bg-gray-800 font-bold uppercase text-xs flex justify-between items-center">
                                    <span>{bucket.team_name}</span>
                                </div>
                                <div className="p-2 space-y-2">
                                    {[1, 2].map(slotNum => {
                                        const driver = slotNum === 1 ? bucket.driver1 : bucket.driver2;
                                        
                                        // FILTER: Nur Fahrer dieses Teams ODER Global Reserves (team_id ist null/leer)
                                        const relevantDrivers = allAvailableDrivers.filter(d => 
                                            d.team_id === bucket.team_id || !d.team_id
                                        );

                                        return (
                                            <div key={slotNum} className="flex items-center gap-2">
                                                <div className="w-6 text-center text-gray-600 text-xs font-bold">{slotNum}</div>
                                                {driver ? (
                                                    <div className="flex-1 bg-gray-800 border border-green-900/50 rounded p-1.5 flex justify-between items-center shadow-inner relative overflow-hidden group">
                                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500"></div>
                                                        <div className="flex items-center gap-2 overflow-hidden pl-2">
                                                            <span className={`text-sm font-bold truncate ${driver.is_reserve ? 'text-yellow-500' : 'text-white'}`}>{driver.username}</span>
                                                        </div>
                                                        <button onClick={() => handleUnassign(bucket.team_id, slotNum as 1|2)} className="text-gray-600 hover:text-red-500 opacity-0 group-hover:opacity-100"><AlertTriangle size={14}/></button>
                                                    </div>
                                                ) : (
                                                    <DriverAutocomplete 
                                                        drivers={relevantDrivers} 
                                                        onSelect={(uid) => handleAssign(uid, bucket.team_id, slotNum as 1|2)}
                                                        placeholder={relevantDrivers.length > 0 ? "Fahrer wählen …" : "Keine Ersatzfahrer"}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-6 flex justify-end items-center border-t border-gray-800 pt-4">
                        <Button onClick={saveGrid} icon={Save}>Startaufstellung speichern</Button>
                    </div>
                </Card>
            </div>
        </div>
    );
};
